// src/lib/dashboard/funilJornadaNoPeriodo.ts
//
// Segunda forma de contar o funil do Comercial: "só o que aconteceu no período".
//
// O funil padrão conta cada etapa pela data DELA (`computeEventFunnel`): o termo
// assinado em outubro entra em outubro mesmo que tenha sido enviado em setembro.
// Aqui a negociação só fica no card se a jornada INTEIRA até aquela etapa — da
// criação do lead em diante — caiu dentro do período. Pedido do Felipe em
// 22/10/2026: ver o mês "de fato", sem o que veio de outro mês.
//
// Consequência: cada card vira um pedaço do anterior (assinado ⊆ enviado ⊆
// simulação ⊆ prospecção), o que o funil por data do evento não garante.
//
// Etapa sem data (pulada) não tira ninguém: a Prefeitura de Morrinhos foi
// aprovada sem simulação nem termo, e o que falta não aconteceu em mês nenhum.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import { inPeriodo } from '../periodoFiltro.ts';
import type { CustomRange, Periodo } from '../periodoFiltro.ts';
import type { EventFunnelRow, EventStage } from '../../../views/CRM/Analytics/eventFunnel.ts';
import { etapasAntes } from './negociacoesDaEtapa.ts';

/**
 * 'evento' = cada etapa pela própria data (o padrão da tela).
 * 'jornada' = só negociações com todas as etapas até a do card dentro do período.
 */
export type ContagemFunil = 'evento' | 'jornada';

/** As etapas ANTERIORES a `key` que a negociação registrou caíram todas no período? */
export function jornadaNoPeriodo(
  r: EventFunnelRow,
  key: string,
  periodo: Periodo,
  customRange: CustomRange,
): boolean {
  return etapasAntes(key).every((d) => {
    const data = d.dataDe(r);
    return !data || inPeriodo(data, periodo, customRange);
  });
}

/**
 * Recorta etapas já calculadas por `computeEventFunnel` (que garantem a data da
 * própria etapa no período) para as negociações com a jornada inteira no período.
 * Com `contagem = 'evento'` devolve as etapas como vieram.
 */
export function aplicarContagemFunil(
  stages: EventStage[],
  contagem: ContagemFunil,
  periodo: Periodo,
  customRange: CustomRange,
): EventStage[] {
  if (contagem !== 'jornada') return stages;
  return stages.map((s) => {
    const rows = s.rows.filter((r) => jornadaNoPeriodo(r, s.key, periodo, customRange));
    return {
      ...s,
      rows,
      leads: rows.length,
      // Mesma soma de `computeEventFunnel`, para o card bater com a lista.
      kwh: rows.reduce((soma, r) => soma + (r.consumo_medio_kwh || 0), 0),
    };
  });
}
